import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  MutableRefObject,
  useRef,
} from 'react';
import { Button, Stack } from '@mui/material';
import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { MediaDevice, EMicrophoneMenuItemKey } from 'shared/types/video.interface';
import ZoomMediaContext from 'zoom/context/media-context';
import { useUnmount, useMount } from 'zoom/hooks';
import { isAndroidOrIOSBrowser } from 'utils/platform';
import {
  DialoutState,
  RecordingStatus,
  MutedSource,
  AudioChangeAction,
  DialOutOption,
  VideoCapturingState,
  MobileVideoFacingMode,
  Participant,
} from '@zoom/videosdk';
import ZoomContext from 'zoom/context/zoom-context';
import { getPhoneCallStatusDescription, LEAVE_BUTTON } from 'shared/constants/zoom-constants';
import { DEBATE_ENDING_OR_LEAVING, CURRENT_DEBATE_ACTIONS } from 'shared/constants/alert-constants';
import { DIALOG_END_DEBATE } from 'shared/constants/modal-constants';
import { urls } from 'shared/constants/routing.constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { openAlertDialog, setSnackbarNotify } from 'redux/slices/modals/modals.slice';
import { ButtonShare } from 'components/Button/ButtonShare/ButtonShare';
import { useCurrentDevicePermissions } from 'shared/hooks/useCurrentDevicePermissions';
import { EPermissionDeviceTypes } from 'shared/types/permission.interface';
import { EnablePermissionsModal } from 'components/Modals/EnablePermissionsModal/EnablePermissionsModal';
import CameraButtonMui from '../Camera/Camera-mui';
import MicrophoneButtonMui from '../Microphone/Microphone-mui';

export interface VideoFooterProps {
  setLogged: (b: boolean) => void;
  visibleParticipants?: Participant[];
  shareRef?: MutableRefObject<HTMLCanvasElement | null>;
  sharing?: boolean;
  isHost?: boolean;
  leaveDebate: (message: string, pageToNagigate?: string) => void;
  captureWidth?: number;
  captureHeight?: number;
}

const isAudioEnable = typeof AudioWorklet === 'function';

const VideoFooter = (props: VideoFooterProps) => {
  const { leaveDebate, captureWidth, captureHeight } = props;
  const dispatch = useAppDispatch();
  const [isStartedAudio, setIsStartedAudio] = useState(false);
  const [isStartedVideo, setIsStartedVideo] = useState(false);
  const isFirstStartVideo = useRef(true);
  const isFirstStartAudio = useRef(true);
  const [audio, setAudio] = useState('');
  const [isSupportPhone, setIsSupportPhone] = useState(false);
  const [phoneCountryList, setPhoneCountryList] = useState<any[]>([]);
  const [phoneCallStatus, setPhoneCallStatus] = useState<DialoutState>();
  const [isStartedScreenShare, setIsStartedScreenShare] = useState(false);
  const [isMirrored, setIsMirrored] = useState(true);
  const [isBlur, setIsBlur] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [activeMicrophone, setActiveMicrophone] = useState('');
  const [activeSpeaker, setActiveSpeaker] = useState('');
  const [activeCamera, setActiveCamera] = useState('');
  const [micList, setMicList] = useState<MediaDevice[]>([]);
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>([]);
  const [cameraList, setCameraList] = useState<MediaDevice[]>([]);
  const [isComputerAudioDisabled, setIsComputerAudioDisabled] = useState(false);
  const { mediaStream } = useContext(ZoomMediaContext);
  const [recordingIsoStatus, setRecordingIsoStatus] = useState<'' | RecordingStatus>('');
  const zmClient = useContext(ZoomContext);

  useCurrentDevicePermissions(EPermissionDeviceTypes.Camera);
  useCurrentDevicePermissions(EPermissionDeviceTypes.Microphone);

  const startVideoWithRetry = useCallback(
    async (retryCount = 0) => {
      try {
        if (mediaStream?.isRenderSelfViewWithVideoElement()) {
          const videoElement = document.querySelector('#video-canvas') as HTMLVideoElement;
          if (videoElement) {
            await mediaStream?.startVideo({
              videoElement,
              hd: true,
              captureWidth,
              captureHeight,
              originalRatio: true,
              mirrored: isMirrored,
              ptz: mediaStream?.isBrowserSupportPTZ(),
            });
          }
        } else {
          await mediaStream?.startVideo({
            hd: true,
            captureWidth,
            captureHeight,
            originalRatio: true,
            mirrored: isMirrored,
            ptz: mediaStream?.isBrowserSupportPTZ(),
          });
        }
        setIsStartedVideo(true);
      } catch (error) {
        if (
          retryCount < 3 &&
          (error?.message?.includes('-101') ||
            error?.message?.includes('video handle is not ready'))
        ) {
          await new Promise((resolve) => {
            setTimeout(resolve, 1000);
          });
          await startVideoWithRetry(retryCount + 1);
        } else {
          window.console.error('Failed to start video after retries:', error);
        }
      }
    },
    [mediaStream, captureWidth, captureHeight, isMirrored],
  );

  const onCameraClick = useCallback(async () => {
    if (isStartedVideo) {
      await mediaStream?.stopVideo();
      setIsStartedVideo(false);
    } else {
      await startVideoWithRetry();
    }
  }, [isStartedVideo, mediaStream, startVideoWithRetry]);

  const turnAudioOn = useCallback(async () => {
    try {
      // Add a small delay to ensure mediaStream is fully ready
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 100);
      });
      await mediaStream.startAudio();
    } catch (error) {
      window.console.error('Failed to start audio:', error);
      setTimeout(async () => {
        try {
          await mediaStream.startAudio();
        } catch (retryError) {
          window.console.error('Failed to start audio on retry: ', retryError);
        }
      }, 1000);
    } finally {
      if (isFirstStartAudio.current) {
        isFirstStartAudio.current = false;
      }
    }
  }, [mediaStream]);

  const onMicrophoneClick = useCallback(async () => {
    if (isStartedAudio) {
      if (isMuted) {
        await mediaStream?.unmuteAudio();
        setIsMuted(false);
      } else {
        await mediaStream?.muteAudio();
        setIsMuted(true);
      }
    } else {
      await turnAudioOn();
    }
  }, [mediaStream, isStartedAudio, isMuted, turnAudioOn]);

  const onMicrophoneMenuClick = async (key: string) => {
    if (mediaStream) {
      const [type, deviceId] = key.split('|');
      if (type === EMicrophoneMenuItemKey.microphone) {
        if (deviceId !== activeMicrophone) {
          await mediaStream.switchMicrophone(deviceId);
          setActiveMicrophone(mediaStream.getActiveMicrophone());
        }
      } else if (type === EMicrophoneMenuItemKey.speaker) {
        if (deviceId !== activeSpeaker) {
          await mediaStream.switchSpeaker(deviceId);
          setActiveSpeaker(mediaStream.getActiveSpeaker());
        }
      } else if (type === EMicrophoneMenuItemKey.leaveAudio) {
        if (!isMuted) {
          await mediaStream.muteAudio();
          setIsMuted(true);
        }
      }
    }
  };

  const onSwitchCamera = async (key: string) => {
    if (mediaStream) {
      if (activeCamera !== key) {
        await mediaStream.switchCamera(key);
        setActiveCamera(mediaStream.getActiveCamera());
      }
    }
  };

  const onMirrorVideo = async () => {
    await mediaStream?.mirrorVideo(!isMirrored);
    setIsMirrored(!isMirrored);
  };

  const onBlurBackground = async () => {
    const vbStatus = mediaStream?.getVirtualbackgroundStatus();
    if (vbStatus?.isVBPreloadReady) {
      if (vbStatus?.isVBConfigured) {
        if (!isBlur) {
          await mediaStream?.updateVirtualBackgroundImage('blur');
        } else {
          await mediaStream?.updateVirtualBackgroundImage(null);
        }
      } else if (!isBlur) {
        await mediaStream?.updateVirtualBackgroundImage('blur');
      } else {
        await mediaStream?.updateVirtualBackgroundImage(null);
      }
      // new code end
      setIsBlur(!isBlur);
    }
  };

  const onPhoneCall = async (
    code: string,
    phoneNumber: string,
    name: string,
    option: DialOutOption,
  ) => {
    await mediaStream?.inviteByPhone(code, phoneNumber, name, option);
  };

  const onPhoneCallCancel = async (
    code: string,
    phoneNumber: string,
    option: { callMe: boolean },
  ) => {
    if (
      [DialoutState.Calling, DialoutState.Ringing, DialoutState.Accepted].includes(
        phoneCallStatus as any,
      )
    ) {
      await mediaStream?.cancelInviteByPhone(code, phoneNumber, option);
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 3000);
      });
    }
    return Promise.resolve();
  };

  const onHostAudioMuted = useCallback(
    (payload) => {
      const { action, source, type } = payload;
      switch (action) {
        case AudioChangeAction.Join:
          setIsStartedAudio(true);
          setAudio(type);
          break;
        case AudioChangeAction.Leave:
          setIsStartedAudio(false);
          break;
        case AudioChangeAction.Muted:
          setIsMuted(true);
          if (source === MutedSource.PassiveByMuteOne) {
            dispatch(
              setSnackbarNotify({
                open: true,
                message: CURRENT_DEBATE_ACTIONS.hostMutedYou,
                severity: 'info',
              }),
            );
          }
          break;
        case AudioChangeAction.Unmuted:
          setIsMuted(false);
          if (source === 'passive') {
            dispatch(
              setSnackbarNotify({
                open: true,
                message: CURRENT_DEBATE_ACTIONS.hostUnmutedYou,
                severity: 'info',
              }),
            );
          }
          break;
        default:
          break;
      }
    },
    [dispatch],
  );

  const onEndClick = useCallback(() => {
    dispatch(
      openAlertDialog({
        title: DIALOG_END_DEBATE.title,
        text: DIALOG_END_DEBATE.text,
        сonsentText: DIALOG_END_DEBATE.сonsentText,
        сonsentCallback: () =>
          leaveDebate(DEBATE_ENDING_OR_LEAVING.youLeft, urls.commonPages.challenges),
        dissentText: DIALOG_END_DEBATE.dissentText,
      }),
    );
  }, [dispatch, leaveDebate]);

  const onPassivelyStopShare = useCallback(({ reason }) => {
    window.console.log('passively stop reason:', reason);
    setIsStartedScreenShare(false);
  }, []);

  const onDeviceChange = useCallback(() => {
    if (mediaStream) {
      setMicList(mediaStream.getMicList());
      setSpeakerList(mediaStream.getSpeakerList());
      if (!isAndroidOrIOSBrowser()) {
        setCameraList(mediaStream.getCameraList());
      }
      setActiveMicrophone(mediaStream.getActiveMicrophone());
      setActiveSpeaker(mediaStream.getActiveSpeaker());
      setActiveCamera(mediaStream.getActiveCamera());
    }
  }, [mediaStream]);

  const onRecordingISOChange = useCallback(
    (payload: any) => {
      if (
        payload?.userId === zmClient.getSessionInfo().userId ||
        payload?.status === RecordingStatus.Ask
      ) {
        setRecordingIsoStatus(payload?.status);
      }
      window.console.log('recording-iso-change', payload);
    },
    [zmClient],
  );

  const onDialOutChange = useCallback((payload) => {
    setPhoneCallStatus(payload.code);
  }, []);

  const onVideoCaptureChange = useCallback((payload) => {
    if (payload.state === VideoCapturingState.Started) {
      setIsStartedVideo(true);
    } else {
      setIsStartedVideo(false);
    }
  }, []);
  const onShareAudioChange = useCallback((payload) => {
    const { state } = payload;
    if (state === 'on') {
      setIsComputerAudioDisabled(true);
    } else if (state === 'off') {
      setIsComputerAudioDisabled(false);
    }
  }, []);
  const onHostAskToUnmute = useCallback((payload) => {
    const { reason } = payload;
    window.console.log('Host ask to unmute the audio.', reason);
  }, []);

  const onCanSeeMyScreen = useCallback(() => {
    dispatch(
      setSnackbarNotify({
        open: true,
        message: CURRENT_DEBATE_ACTIONS.usersCanSeeYourScreen,
        severity: 'info',
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    zmClient.on('current-audio-change', onHostAudioMuted);
    zmClient.on('passively-stop-share', onPassivelyStopShare);
    zmClient.on('device-change', onDeviceChange);
    zmClient.on('individual-recording-change', onRecordingISOChange);
    zmClient.on('dialout-state-change', onDialOutChange);
    zmClient.on('Video-capturing-change', onVideoCaptureChange);
    zmClient.on('share-audio-change', onShareAudioChange);
    zmClient.on('host-ask-unmute-audio', onHostAskToUnmute);
    zmClient.on('share-can-see-screen', onCanSeeMyScreen);
    return () => {
      zmClient.off('current-audio-change', onHostAudioMuted);
      zmClient.off('passively-stop-share', onPassivelyStopShare);
      zmClient.off('device-change', onDeviceChange);
      zmClient.off('individual-recording-change', onRecordingISOChange);
      zmClient.off('dialout-state-change', onDialOutChange);
      zmClient.off('Video-capturing-change', onVideoCaptureChange);
      zmClient.off('share-audio-change', onShareAudioChange);
      zmClient.off('host-ask-unmute-audio', onHostAskToUnmute);
      zmClient.off('share-can-see-screen', onCanSeeMyScreen);
    };
  }, [
    zmClient,
    onHostAudioMuted,
    onPassivelyStopShare,
    onDeviceChange,
    onDialOutChange,
    onVideoCaptureChange,
    onShareAudioChange,
    onHostAskToUnmute,
    onCanSeeMyScreen,
    onRecordingISOChange,
  ]);
  useUnmount(() => {
    if (isStartedAudio) {
      mediaStream?.stopAudio();
    }
    if (isStartedVideo) {
      mediaStream?.stopVideo();
    }
    if (isStartedScreenShare) {
      mediaStream?.stopShareScreen();
    }
  });
  useMount(() => {
    if (mediaStream) {
      setIsSupportPhone(!!mediaStream.isSupportPhoneFeature());
      setPhoneCountryList(mediaStream.getSupportCountryInfo() || []);
      if (isAndroidOrIOSBrowser()) {
        setCameraList([
          { deviceId: MobileVideoFacingMode.User, label: 'Front-facing' },
          { deviceId: MobileVideoFacingMode.Environment, label: 'Rear-facing' },
        ]);
      }
    }
  });
  useEffect(() => {
    if (mediaStream && !isStartedAudio && zmClient?.getSessionInfo()?.isInMeeting) {
      turnAudioOn();
    }
  }, [mediaStream, isStartedAudio, zmClient, turnAudioOn]);

  useEffect(() => {
    if (
      mediaStream &&
      !isFirstStartAudio.current &&
      !isStartedVideo &&
      isFirstStartVideo.current &&
      zmClient?.getSessionInfo()?.isInMeeting
    ) {
      isFirstStartVideo.current = false;
      startVideoWithRetry();
    }
  }, [mediaStream, isStartedVideo, zmClient, startVideoWithRetry, isFirstStartAudio]);

  useEffect(() => {
    if (mediaStream && zmClient.getSessionInfo().isInMeeting) {
      mediaStream.subscribeAudioStatisticData();
      mediaStream.subscribeVideoStatisticData();
    }
    return () => {
      if (zmClient.getSessionInfo().isInMeeting) {
        mediaStream?.unsubscribeAudioStatisticData();
        mediaStream?.unsubscribeVideoStatisticData();
      }
    };
  }, [mediaStream, zmClient]);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      sx={{
        columnGap: { lg: '16px', md: '8px', sm: '8px', xs: '8px' },
        '@media (max-width:500px)': {
          '& .button-share': {
            fontSize: 0,
            padding: 0,
            minWidth: '44px',
            width: '44px',
            '& svg': {
              margin: 0,
            },
          },
        },
      }}
    >
      {isAudioEnable && (
        <MicrophoneButtonMui
          isStartedAudio={isStartedAudio}
          isMuted={isMuted}
          isSupportPhone={isSupportPhone}
          audio={audio}
          phoneCountryList={phoneCountryList}
          onPhoneCallClick={onPhoneCall}
          onPhoneCallCancel={onPhoneCallCancel}
          phoneCallStatus={getPhoneCallStatusDescription(phoneCallStatus)}
          onMicrophoneClick={onMicrophoneClick}
          onMicrophoneMenuClick={onMicrophoneMenuClick}
          microphoneList={micList}
          speakerList={speakerList}
          activeMicrophone={activeMicrophone}
          activeSpeaker={activeSpeaker}
          disabled={isComputerAudioDisabled}
        />
      )}
      <CameraButtonMui
        isStartedVideo={isStartedVideo}
        onCameraClick={onCameraClick}
        onSwitchCamera={onSwitchCamera}
        onMirrorVideo={onMirrorVideo}
        onBlurBackground={onBlurBackground}
        cameraList={cameraList}
        activeCamera={activeCamera}
        isMirrored={isMirrored}
        isBlur={isBlur}
      />
      <ButtonShare
        shareUrl={`${window.location.origin}${urls.commonPages.debateCard}/${localStorage.getItem(
          LOCAL_STORAGE.currentDebateId,
        )}`}
        title={localStorage.getItem(LOCAL_STORAGE.currentDebateTitle) || ''}
      />
      <Button variant="leave" onClick={onEndClick}>
        {LEAVE_BUTTON.endDebate}
      </Button>
      {recordingIsoStatus === RecordingStatus.Ask && null}
      <EnablePermissionsModal />
    </Stack>
  );
};
export default VideoFooter;
